import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { MainLayout } from 'components';
import InternalContainer from 'components/internal-container/internal-container';
import FileInput from 'components/CampanhasMkt-components/fileInput';
import DataInput from 'components/CampanhasMkt-components/dataInput';
import CampaignSelect from 'components/CampanhasMkt-components/campaignSelect';
import DefaultFileLink from 'components/CampanhasMkt-components/CampanhasDefaultFileLink/defaultFileLink';
import DefaultFileModal from 'components/CampanhasMkt-components/defaultFileModal/default-file-modal';
import CampaignHelpModal from 'components/CampanhasMkt-components/campaignHelpModal';
import { CampanhaValues } from 'models/campanhas';
import {
  CampaignCreateLocationState,
  CampaignHelpModalMode,
} from 'models/campaign-mkt';
import {
  contextosCampanha,
  duracaoCampanha,
  graus,
  publicosCampanha,
} from 'utils/campanha-utils';
import { useHistory, useLocation } from 'react-router-dom';
import { UseCampanhaHook } from 'hooks/campanha-hook';
import { useCreateCampaign } from 'hooks/create-campaign';
import {
  ErrorModal,
  ValueSendedModal,
} from '@fundacao/shared-components/esm/ie/';
import { UseCampanhaContext } from 'context/campanha-context';
import HelpIcon from 'assets/img/campanhas-de-marketing/nova-campanha/icon-help.svg';
import DownloadIcon from 'assets/img/campanhas-de-marketing/nova-campanha/icon-download.svg';
import ExcelImage from 'assets/img/campanhas-de-marketing/nova-campanha/image-excel.svg';
import * as S from './style';

function CriarCampanha(): JSX.Element {
  const location = useLocation<CampaignCreateLocationState>();
  const [publico, setPublico] = useState(() => location.state?.audience || '');
  const [publicoError, setPublicoError] = useState(false);
  const history = useHistory();
  const [helpModalMode, setHelpModalMode] = useState<CampaignHelpModalMode>();
  const {
    defaultFileModalOpen,
    formCampanhaErrors,
    formCampanhaValues,
  } = UseCampanhaContext();
  const {
    handleDropdownChange,
    handleInputChange,
    handleInputError,
  } = UseCampanhaHook();
  const {
    downloadCampaignResult,
    file,
    fileError,
    handleFileChange,
    isFormInvalid,
    isSubmitting,
    isUploading,
    modalInformation,
    open,
    progress,
    setFileError,
    showErrorModal,
    submitCampaign,
  } = useCreateCampaign(publico);

  useEffect(() => {
    const preloadImage = new Image();
    preloadImage.src = ExcelImage;
  }, []);

  const contextOptions = useMemo(() => contextosCampanha[publico] || [], [
    publico,
  ]);

  const handlePublicoChange = (value: string) => {
    setPublico(value);
    setPublicoError(false);
    handleInputChange(CampanhaValues.TIPO_DE_CAMPANHA, '');
    handleInputError(CampanhaValues.TIPO_DE_CAMPANHA, false);
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!isFormInvalid && !isSubmitting) await submitCampaign();
  };

  return (
    <MainLayout>
      <InternalContainer
        direction="column"
        fullHeight
        align="center"
        padding="0 30px"
      >
        <S.StyledContainer>
          {modalInformation.length > 0 && (
            <ValueSendedModal
              isOpen={open}
              onClick={downloadCampaignResult}
              onClose={() => history.push('/mkt-campanhas')}
              title="Resultado do envio da campanha"
              warningMessage="*Essas linhas não foram enviadas. Baixe os arquivos para fazer as correções indicadas no relatório e reenvie o arquivo CSV criando uma nova campanha."
              returnImport={modalInformation}
            />
          )}
          {defaultFileModalOpen && <DefaultFileModal />}
          {helpModalMode && (
            <CampaignHelpModal
              audience={publico}
              mode={helpModalMode}
              onClose={() => setHelpModalMode(undefined)}
              show
            />
          )}
          <S.PageHeader>
            <S.BackButton
              type="button"
              onClick={() => history.push('/mkt-campanhas')}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Voltar
            </S.BackButton>
            <S.PageTitle>Nova campanha</S.PageTitle>
          </S.PageHeader>
          <S.PaperStyled>
            <form onSubmit={handleSubmit} noValidate>
              <S.FormFields>
                <S.FieldGroup>
                  <CampaignSelect
                    error={publicoError}
                    label="Público da campanha"
                    onBlur={(value) => setPublicoError(!value)}
                    onChange={handlePublicoChange}
                    options={publicosCampanha}
                    value={publico}
                  />
                  {publicoError && (
                    <S.FieldError>
                      Campo obrigatório não preenchido
                    </S.FieldError>
                  )}
                </S.FieldGroup>
                <S.FieldGroup>
                  <CampaignSelect
                    disabled={!publico}
                    error={formCampanhaErrors[CampanhaValues.TIPO_DE_CAMPANHA]}
                    label="Contexto do público"
                    onBlur={(value) =>
                      handleInputError(CampanhaValues.TIPO_DE_CAMPANHA, !value)
                    }
                    onChange={(value) => {
                      handleInputChange(CampanhaValues.TIPO_DE_CAMPANHA, value);
                      handleInputError(CampanhaValues.TIPO_DE_CAMPANHA, false);
                    }}
                    options={contextOptions}
                    value={formCampanhaValues[CampanhaValues.TIPO_DE_CAMPANHA]}
                  />
                  {formCampanhaErrors[CampanhaValues.TIPO_DE_CAMPANHA] && (
                    <S.FieldError>
                      Campo obrigatório não preenchido
                    </S.FieldError>
                  )}
                  <S.ActionLinks>
                    <button
                      aria-haspopup="dialog"
                      onClick={() => {
                        if (!publico) {
                          setPublicoError(true);
                          return;
                        }
                        setHelpModalMode('audience');
                      }}
                      type="button"
                    >
                      <img src={HelpIcon} alt="" />
                      Entenda cada opção
                    </button>
                  </S.ActionLinks>
                </S.FieldGroup>
                <S.FieldGroup>
                  <CampaignSelect
                    error={formCampanhaErrors[CampanhaValues.GRAU_DE_ENSINO]}
                    label="Grau de ensino"
                    onBlur={(value) =>
                      handleInputError(CampanhaValues.GRAU_DE_ENSINO, !value)
                    }
                    onChange={(value) =>
                      handleDropdownChange(
                        value,
                        graus,
                        CampanhaValues.GRAU_DE_ENSINO,
                      )
                    }
                    options={graus}
                    value={formCampanhaValues[CampanhaValues.GRAU_DE_ENSINO]}
                  />
                  {formCampanhaErrors[CampanhaValues.GRAU_DE_ENSINO] && (
                    <S.FieldError>
                      Campo obrigatório não preenchido
                    </S.FieldError>
                  )}
                </S.FieldGroup>
                <S.FieldGroup>
                  <DataInput label="Início da campanha" />
                </S.FieldGroup>
                <S.FieldGroup>
                  <S.StyledTextField
                    $error={formCampanhaErrors[CampanhaValues.NOME_CAMPANHA]}
                    $filled={Boolean(
                      formCampanhaValues[CampanhaValues.NOME_CAMPANHA],
                    )}
                  >
                    <S.FieldLabel htmlFor="campaign-name">
                      Nome da campanha
                    </S.FieldLabel>
                    <input
                      id="campaign-name"
                      value={
                        formCampanhaValues[CampanhaValues.NOME_CAMPANHA] || ''
                      }
                      onChange={(event) => {
                        handleInputChange(
                          CampanhaValues.NOME_CAMPANHA,
                          event.target.value,
                        );
                        handleInputError(
                          CampanhaValues.NOME_CAMPANHA,
                          !event.target.value.trim(),
                        );
                      }}
                      onBlur={() =>
                        handleInputError(
                          CampanhaValues.NOME_CAMPANHA,
                          !formCampanhaValues[CampanhaValues.NOME_CAMPANHA],
                        )
                      }
                    />
                  </S.StyledTextField>
                  {formCampanhaErrors[CampanhaValues.NOME_CAMPANHA] && (
                    <S.FieldError>
                      Campo obrigatório não preenchido
                    </S.FieldError>
                  )}
                </S.FieldGroup>
                <S.FieldGroup>
                  <CampaignSelect
                    error={formCampanhaErrors[CampanhaValues.DURACAO]}
                    label="Duração da campanha"
                    onBlur={(value) =>
                      handleInputError(CampanhaValues.DURACAO, !value)
                    }
                    onChange={(value) =>
                      handleDropdownChange(
                        value,
                        duracaoCampanha,
                        CampanhaValues.DURACAO,
                      )
                    }
                    options={duracaoCampanha}
                    value={String(
                      formCampanhaValues[CampanhaValues.DURACAO] || '',
                    )}
                  />
                  {formCampanhaErrors[CampanhaValues.DURACAO] && (
                    <S.FieldError>
                      Campo obrigatório não preenchido
                    </S.FieldError>
                  )}
                </S.FieldGroup>
                <S.FieldGroup>
                  <FileInput
                    file={file}
                    fileError={fileError}
                    progress={progress}
                    setFile={handleFileChange}
                    setFileError={setFileError}
                    uploading={isUploading}
                  />
                  <S.ActionLinks $upload>
                    <button
                      aria-haspopup="dialog"
                      onClick={() => setHelpModalMode('upload')}
                      type="button"
                    >
                      <img src={HelpIcon} alt="" />
                      Entenda como enviar
                    </button>
                    <DefaultFileLink onModal>
                      <img src={DownloadIcon} alt="" />
                      Baixe o modelo de planilha
                    </DefaultFileLink>
                  </S.ActionLinks>
                </S.FieldGroup>
              </S.FormFields>
              <S.FormActions>
                <S.CreateButton
                  type="submit"
                  disabled={isFormInvalid || isSubmitting}
                >
                  Criar campanha
                </S.CreateButton>
                <S.CancelButton
                  type="button"
                  onClick={() => history.push('/mkt-campanhas')}
                >
                  Cancelar
                </S.CancelButton>
              </S.FormActions>
            </form>
          </S.PaperStyled>
        </S.StyledContainer>
      </InternalContainer>
      {showErrorModal && (
        <ErrorModal
          isOpen={showErrorModal}
          title="Erro ao criar campanha"
          bodyMessage="Tente novamente."
          primaryButton={{
            label: 'Voltar',
            onClick: () => history.push('/mkt-campanhas'),
          }}
        />
      )}
    </MainLayout>
  );
}

export default CriarCampanha;
